import { ACHIEVEMENTS_CONFIG } from '../constants.js';

const T = ACHIEVEMENTS_CONFIG.THRESHOLDS;

export const ACHIEVEMENT_CATEGORIES = [
  'exploration',
  'trading',
  'social',
  'survival',
  'danger',
  'moral',
];

export const ACHIEVEMENTS = [
  // Exploration: systems visited
  {
    id: 'exploration_1',
    name: 'First Steps',
    description: `Visit ${T.EXPLORATION_TIER_1} star systems`,
    category: 'exploration',
    tier: 1,
    target: T.EXPLORATION_TIER_1,
    statPath: 'world.visitedSystems.length',
  },
  {
    id: 'exploration_2',
    name: 'Pathfinder',
    description: `Visit ${T.EXPLORATION_TIER_2} star systems`,
    category: 'exploration',
    tier: 2,
    target: T.EXPLORATION_TIER_2,
    statPath: 'world.visitedSystems.length',
  },
  {
    id: 'exploration_3',
    name: 'Star Mapper',
    description: `Visit ${T.EXPLORATION_TIER_3} star systems`,
    category: 'exploration',
    tier: 3,
    target: T.EXPLORATION_TIER_3,
    statPath: 'world.visitedSystems.length',
  },
  {
    id: 'exploration_4',
    name: 'Galaxy Wanderer',
    description: 'Visit every reachable star system',
    category: 'exploration',
    tier: 4,
    target: T.EXPLORATION_TIER_4,
    statPath: 'world.visitedSystems.length',
  },

  // Trading: credits earned
  {
    id: 'trading_1',
    name: 'Petty Cash',
    description: `Earn ${T.TRADING_TIER_1.toLocaleString()} credits from trading`,
    category: 'trading',
    tier: 1,
    target: T.TRADING_TIER_1,
    statPath: 'stats.creditsEarned',
  },
  {
    id: 'trading_2',
    name: 'Merchant',
    description: `Earn ${T.TRADING_TIER_2.toLocaleString()} credits from trading`,
    category: 'trading',
    tier: 2,
    target: T.TRADING_TIER_2,
    statPath: 'stats.creditsEarned',
  },
  {
    id: 'trading_3',
    name: 'Trade Baron',
    description: `Earn ${T.TRADING_TIER_3.toLocaleString()} credits from trading`,
    category: 'trading',
    tier: 3,
    target: T.TRADING_TIER_3,
    statPath: 'stats.creditsEarned',
  },
  {
    id: 'trading_4',
    name: 'Tycoon',
    description: `Earn ${T.TRADING_TIER_4.toLocaleString()} credits from trading`,
    category: 'trading',
    tier: 4,
    target: T.TRADING_TIER_4,
    statPath: 'stats.creditsEarned',
  },

  // Social: trusted NPCs (rep >= TRUSTED_MIN)
  {
    id: 'social_1',
    name: 'First Friend',
    description: `Earn Trusted status with ${T.SOCIAL_TIER_1} ${T.SOCIAL_TIER_1 === 1 ? 'NPC' : 'NPCs'}`,
    category: 'social',
    tier: 1,
    target: T.SOCIAL_TIER_1,
    statPath: 'computed.trustedNPCCount',
  },
  {
    id: 'social_2',
    name: 'Inner Circle',
    description: `Earn Trusted status with ${T.SOCIAL_TIER_2} NPCs`,
    category: 'social',
    tier: 2,
    target: T.SOCIAL_TIER_2,
    statPath: 'computed.trustedNPCCount',
  },
  {
    id: 'social_3',
    name: 'Networker',
    description: `Earn Trusted status with ${T.SOCIAL_TIER_3} NPCs`,
    category: 'social',
    tier: 3,
    target: T.SOCIAL_TIER_3,
    statPath: 'computed.trustedNPCCount',
  },
  {
    id: 'social_4',
    name: 'Beloved',
    description: `Earn Trusted status with ${T.SOCIAL_TIER_4} NPCs`,
    category: 'social',
    tier: 4,
    target: T.SOCIAL_TIER_4,
    statPath: 'computed.trustedNPCCount',
  },

  // Survival: jumps completed
  {
    id: 'survival_1',
    name: 'Rookie Pilot',
    description: `Complete ${T.SURVIVAL_TIER_1} jumps`,
    category: 'survival',
    tier: 1,
    target: T.SURVIVAL_TIER_1,
    statPath: 'stats.jumpsCompleted',
  },
  {
    id: 'survival_2',
    name: 'Seasoned Captain',
    description: `Complete ${T.SURVIVAL_TIER_2} jumps`,
    category: 'survival',
    tier: 2,
    target: T.SURVIVAL_TIER_2,
    statPath: 'stats.jumpsCompleted',
  },
  {
    id: 'survival_3',
    name: 'Wormhole Veteran',
    description: `Complete ${T.SURVIVAL_TIER_3} jumps`,
    category: 'survival',
    tier: 3,
    target: T.SURVIVAL_TIER_3,
    statPath: 'stats.jumpsCompleted',
  },
  {
    id: 'survival_4',
    name: 'Void Walker',
    description: `Complete ${T.SURVIVAL_TIER_4} jumps`,
    category: 'survival',
    tier: 4,
    target: T.SURVIVAL_TIER_4,
    statPath: 'stats.jumpsCompleted',
  },

  // Danger: total danger encounters resolved
  {
    id: 'danger_1',
    name: 'Survivor',
    description: `Resolve ${T.DANGER_TIER_1} danger encounters`,
    category: 'danger',
    tier: 1,
    target: T.DANGER_TIER_1,
    statPath: 'computed.totalDangerEncounters',
  },
  {
    id: 'danger_2',
    name: 'Battle-Scarred',
    description: `Resolve ${T.DANGER_TIER_2} danger encounters`,
    category: 'danger',
    tier: 2,
    target: T.DANGER_TIER_2,
    statPath: 'computed.totalDangerEncounters',
  },
  {
    id: 'danger_3',
    name: 'Hardened',
    description: `Resolve ${T.DANGER_TIER_3} danger encounters`,
    category: 'danger',
    tier: 3,
    target: T.DANGER_TIER_3,
    statPath: 'computed.totalDangerEncounters',
  },
  {
    id: 'danger_4',
    name: 'Unkillable',
    description: `Resolve ${T.DANGER_TIER_4} danger encounters`,
    category: 'danger',
    tier: 4,
    target: T.DANGER_TIER_4,
    statPath: 'computed.totalDangerEncounters',
  },

  // Moral: karma absolute value (works for both saint and villain paths)
  {
    id: 'moral_1',
    name: 'Picking Sides',
    description: `Reach karma magnitude of ${T.MORAL_TIER_1}`,
    category: 'moral',
    tier: 1,
    target: T.MORAL_TIER_1,
    statPath: 'computed.karmaAbsolute',
  },
  {
    id: 'moral_2',
    name: 'Committed',
    description: `Reach karma magnitude of ${T.MORAL_TIER_2}`,
    category: 'moral',
    tier: 2,
    target: T.MORAL_TIER_2,
    statPath: 'computed.karmaAbsolute',
  },
  {
    id: 'moral_3',
    name: 'True Believer',
    description: `Reach karma magnitude of ${T.MORAL_TIER_3}`,
    category: 'moral',
    tier: 3,
    target: T.MORAL_TIER_3,
    statPath: 'computed.karmaAbsolute',
  },
  {
    id: 'moral_4',
    name: 'Legend',
    description: `Reach karma magnitude of ${T.MORAL_TIER_4}`,
    category: 'moral',
    tier: 4,
    target: T.MORAL_TIER_4,
    statPath: 'computed.karmaAbsolute',
  },
];
